// Reducer managing devices map keyed by coreid
// Actions: INITIAL_SNAPSHOT (array), AGGREGATED_UPDATE (single update), RESET
// New movement tracker (session-based preset transitions)
import { movementStateFromAggregated, applyEventsToMovement, emptyMovementState } from '../logic/movementTracker';
import { SYSTEM_TYPES } from '../constants';
import { updateFromDevice } from '../utils/systemTypeCache';

// Movement related keys we care about for timestamps when reconstructing from aggregated snapshot
const MOVEMENT_KEYS = [ 'p/auto/stop', 'p/bed/moving', 'r/bed/moving', 'p/bed/stop', 'p/system_state', 'p/motor/stop', 'p/motor/auto/stop', 'k/status']; //'p/auto/button', 'r/auto/button',

// Unified events schema parser: events: [ [key, value, timestamp], ... ]
function parseEventsArray(events) {
  const aggregatedData = {};
  const perKeyTimestamps = {};
  if (Array.isArray(events)) {
    for (const tuple of events) {
      if (!Array.isArray(tuple) || tuple.length < 3) continue;
      const [key, value, ts] = tuple;
      if (typeof key !== 'string') continue;
      aggregatedData[key] = value;
      perKeyTimestamps[key] = ts;
    }
  }
  return { aggregatedData, perKeyTimestamps };
}

export function devicesReducer(state, action) {
  switch (action.type) {
    case 'INITIAL_SNAPSHOT':
      return mergeSnapshot(state, action.payload || []);
    case 'AGGREGATED_UPDATE':
      return mergeUpdate(state, action.payload);
    case 'RESET':
      return {};
    default:
      return state;
  }
}

export function indexSnapshot(list) {
  const map = {};
  list.forEach(d => {
    if (!d || !d.coreid) return;
    const { coreid, fw_version, productid, userid, published_at, events, sfdc_data } = d;
    const { aggregatedData, perKeyTimestamps } = parseEventsArray(events);
    // Build movementEvents subset for movementStateFromAggregated convenience
    const movementEvents = {};
    MOVEMENT_KEYS.forEach(k => { if (perKeyTimestamps[k]) movementEvents[k] = perKeyTimestamps[k]; });
    const systemTypeLabel = SYSTEM_TYPES[Number(aggregatedData['r/system_type'])] || null;
    const deviceObj = {
      coreid,
      fw_version: fw_version || '',
      productid: productid || '',
      userid: userid || '',
      aggregatedData,
      published_at: published_at || '',
      // Initialize movement state from aggregated snapshot
      movement: movementStateFromAggregated(aggregatedData, movementEvents, systemTypeLabel)
    };
    // Include sfdc_data if present
    if (sfdc_data) {
      deviceObj.sfdc_data = sfdc_data;
    }
    map[coreid] = deviceObj;
    try { updateFromDevice(deviceObj); } catch (_) { }
  });
  return map;
}

// Merge new snapshot batch into existing state without discarding previously received devices.
function mergeSnapshot(state, list) {
  if (!Array.isArray(list) || list.length === 0) return state;
  const incoming = indexSnapshot(list);
  const merged = { ...state, ...incoming };
  if (import.meta.env.MODE !== 'production') {
    // Debug counts
    // eslint-disable-next-line no-console
    console.debug('[devicesReducer] mergeSnapshot batch=', list.length, 'total=', Object.keys(merged).length);
  }
  return merged;
}

export function mergeUpdate(state, payload) {
  if (!payload) return state;
  const { coreid, events, fw_version, productid, userid, published_at } = payload;
  if (!coreid || !Array.isArray(events)) return state;
  const current = state[coreid] || {
    coreid,
    fw_version: '',
    productid: '',
    userid: '',
    aggregatedData: {},
    published_at: ''
  };
  const { aggregatedData: incomingAgg } = parseEventsArray(events);
  const updatedAggregated = { ...current.aggregatedData, ...incomingAgg };
  // Extract movement-related event tuples only from incoming events for incremental movement update
  const movementEventTuples = events.filter(tuple => Array.isArray(tuple) && MOVEMENT_KEYS.includes(tuple[0]));
  const updated = {
    ...current,
    fw_version: fw_version || current.fw_version,
    productid: productid || current.productid,
    userid: userid || current.userid,
    aggregatedData: updatedAggregated,
    published_at: published_at || current.published_at
  };
  const systemTypeLabel = SYSTEM_TYPES[Number(updatedAggregated['r/system_type'])] || null;
  const movement = applyEventsToMovement(current.movement || emptyMovementState(['Cloud Bed','Cloud Bed Table','Pocket Studio'].includes(systemTypeLabel)), movementEventTuples, systemTypeLabel);
  const finalDevice = { ...updated, movement };
  try { updateFromDevice(finalDevice); } catch (_) { }
  return { ...state, [coreid]: finalDevice };
}


// Legacy movementEvents tracking removed; movement state relies solely on event tuples.
